import {
  GOTO_ROOT_ID,
  GOTO_TOPBAR_CONTAINER_ID,
  GOTO_NAVIGATION_ID,
  GOTO_CONTENT_ID,
  GOTO_BANNER_NOTIFICATION_ID,
  GOTO_SNACKBAR_NOTIFICATION_ID,
  GOTO_MODAL_NOTIFICATION_ID,
  CHAMELEON_THEME_PROVIDER,
  CHAMELEON_LAUNCH_SCREEN,
  GOTO_SPECIFIC_AREA,
  GOTO_DIALOG_CONTAINER_ID,
} from './container'
import { getShellLogger } from './logger'

const addFavicon = (faviconPath: string) => {
  const document = getDocument()
  const iconElement = document.createElement('link')
  iconElement.rel = 'icon'
  iconElement.type = 'image/x-icon'
  iconElement.href = faviconPath
  iconElement.id = 'dynamic-favicon'
  document.head.prepend(iconElement)
}

const removeFavicon = () => {
  const favicon = document.getElementById('dynamic-favicon')
  if (favicon) {
    document.head.removeChild(favicon)
  }
}

export const setFavicon = (faviconPath: string) => {
  removeFavicon()
  addFavicon(faviconPath)
}

const createElement = (tagName: string, id?: string, classes?: readonly string[]): HTMLElement => {
  const element = getDocument().createElement(tagName)

  if (id) {
    element.id = id
  }

  if (classes) {
    element.classList.add(...classes)
  }

  return element
}

export const getLocationHREF = () => getWindow().location.href
export const getLocationOrigin = () => getWindow().location.origin
export const getLocationPathname = () => getWindow().location.pathname
export const getLocationHash = () => getWindow().location.hash
export const getLocationSearch = () => getWindow().location.search
export const getLocationSearchParamsByName = (name: string) => {
  const searchParams = new URLSearchParams(getLocationSearch())
  return searchParams.get(name)
}
export const getLocationHostname = () => getWindow().location.hostname
export const getLocationProtocol = () => getWindow().location.protocol

export const setLocationHref = (url: string) => {
  getWindow().location.href = url
}

export const postMessageOnMainWindow = <T>(message: T, targetOrigin: string) =>
  getWindow().top?.postMessage(message, targetOrigin)

export const createGotoRootElement = () => createElement('goto-shell-controller', GOTO_ROOT_ID)
export const createTopBarContainer = () => createElement('header', GOTO_TOPBAR_CONTAINER_ID)
export const createInspectorPanelContainer = () => createElement('goto-inspector-panel')
export const createNavigationElement = () => createElement('div', GOTO_NAVIGATION_ID)
export const createMainElement = () => createElement('main', undefined, ['goto-container'])
// Hiden until we can get the branding FeatureFlag value
export const createChameleonLauncherScreenElement = () => {
  const elem = createElement(CHAMELEON_LAUNCH_SCREEN)
  elem.setAttribute('style', 'display:none;')
  return elem
}

export const createBannerNotificationElement = () => createElement('div', GOTO_BANNER_NOTIFICATION_ID)
export const createTrialBannerElement = () => createElement('goto-trial-banner')
export const createExperienceContentElement = () => createElement('div', GOTO_CONTENT_ID)
export const createSnackbarNotificationElement = () => createElement('div', GOTO_SNACKBAR_NOTIFICATION_ID)
export const createModalNotificationElement = () => createElement('div', GOTO_MODAL_NOTIFICATION_ID)
export const createDialogContainer = () => createElement('div', GOTO_DIALOG_CONTAINER_ID)

export const createChameleonThemeProviderElement = () => createElement(CHAMELEON_THEME_PROVIDER)
export const createSpecificAreaElement = () => createElement('div', GOTO_SPECIFIC_AREA)

export const getWindow = (): Window => window
export const getDocument = (): Document => document

export const getDocumentReferrer = () => getDocument().referrer
export const getDocumentHead = () => getDocument().head

/**
 * Assigns the location to the specified url.
 * If the current location matches the destination url and the url contains a hash, a force reload is applied.
 * @param url destination url
 */
export const navigateToExternal = (url: string) => {
  const location = getWindow().location
  try {
    const destination = new URL(url)
    const forceReload =
      location.origin === destination.origin && location.pathname === destination.pathname && !!destination.hash
    getWindow().location.assign(url)
    if (forceReload) {
      getWindow().location.reload()
    }
  } catch (e) {
    getShellLogger().error(`Improper URL passed to the function: ${url}, ${e}`)
  }
}

export const getActiveUrlPath = () => {
  const pathname = getLocationPathname() ?? ''
  const search = getLocationSearch() ?? ''
  const hash = getLocationHash() ?? ''
  return `${pathname}${search}${hash}`
}

export const getFromLocalStorage = (key: string) => getWindow().localStorage.getItem(key)
export const setToLocalStorage = (key: string, value: string) => getWindow().localStorage.setItem(key, value)
export const removeFromLocalStorage = (key: string) => getWindow().localStorage.removeItem(key)

export const popFromLocalStorage = (key: string) => {
  const value = getFromLocalStorage(key)
  removeFromLocalStorage(key)
  return value
}
export const setToSessionStorage = (key: string, value: string) => getWindow().sessionStorage.setItem(key, value)
export const getFromSessionStorage = (key: string) => getWindow().sessionStorage.getItem(key)
export const removeFromSessionStorage = (key: string) => getWindow().sessionStorage.removeItem(key)
export const popFromSessionStorage = (key: string) => {
  const value = getFromSessionStorage(key)
  removeFromSessionStorage(key)
  return value
}

export const setTimeout = (callback: () => void, intervalMs: number) => getWindow().setTimeout(callback, intervalMs)
export const setInterval = (callback: () => void, intervalMs: number) => getWindow().setInterval(callback, intervalMs)
export const clearTimeout = (handler: number) => getWindow().clearTimeout(handler)

/*
 * This will open the URL in a new tab or in the case of electron container in the browser
 */
export const openNewTabFor = (url: string) => getWindow().open(url, '_blank', 'noreferrer')

export const openNewWindowFor = (url: string, newWindowOptions: NewWindowOptions) =>
  getWindow().open(
    url,
    '_blank',
    `rel=noreferrer,
    height=${newWindowOptions.height},
    width=${newWindowOptions.width},
    left=${newWindowOptions.left},
    top=${newWindowOptions.top}`,
  )

export const reloadPage = () => {
  getWindow().location.reload()
}

const observerConfig = {
  attributes: false,
  childList: true,
  characterData: true,
  subtree: true,
}

export const waitForElement = (selector: string, parent: Element): Promise<void> =>
  new Promise(resolve => {
    const element = parent.querySelectorAll(selector)
    if (element.length > 0) {
      resolve()
    } else {
      const observer = new MutationObserver((mutations, obs) => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE && (node as Element).matches(selector)) {
              obs.disconnect()
              resolve()
            }
          })
        })
      })
      observer.observe(parent, observerConfig)
    }
  })

export const isInIframe = () => window.self !== window.top

export interface NewWindowOptions {
  readonly height: string
  readonly width: string
  readonly left: string
  readonly top: string
}
